import { InfoCircleOutlined, SendOutlined } from '@ant-design/icons';
import { Modal, ModalProps, Typography } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';

import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import usePrefetchPages from '../hooks/usePrefetchPages';
import { JobPostingDetail } from '../types';
import EmailForm, { EmailFormValues } from './EmailForm';
import Form from './Form';

export interface SendAdditionalQuestionsModalProps extends ModalProps {
  jobPostingId?: JobPostingDetail['id'];
  loading?: boolean;
  helmetTitle: string;
  onFinish?: (values: EmailFormValues) => void;
  hasCandidatesThatAnswered: () => Promise<boolean>;
}

interface ConfirmSendPendingAdditionalQuestionsModalProps extends ModalProps {
  onSuccess?: () => void | Promise<unknown>;
}

const ConfirmSendPendingAdditionalQuestionsModal = ({
  ...props
}: ConfirmSendPendingAdditionalQuestionsModalProps) => {
  return (
    <Modal
      okText="Enviar somente para pendentes"
      title={
        <Typography.Text>
          <InfoCircleOutlined style={{ color: 'red', marginRight: '2%' }} />
          Perguntas de pré-requisito respondidas.
        </Typography.Text>
      }
      {...props}
    >
      Alguns candidato(s) selecionado(s) já responderam as perguntas de pré-requisito para a vaga.
    </Modal>
  );
};

const SendAdditionalQuestionsModal = ({
  jobPostingId,
  loading,
  helmetTitle,
  onFinish,
  hasCandidatesThatAnswered,
  ...props
}: SendAdditionalQuestionsModalProps) => {
  const [form] = Form.useForm();
  const [
    confirmSendPendingAdditionalQuestionsModalIsOpen,
    setConfirmSendPendingAdditionalQuestionsModalIsOpen,
  ] = useState(false);

  const config = useAPIQuery('/configs/tenant-configurations/');

  const user = useAPIQuery('/users/me/');

  const emailCommunicationTemplatesPages = useInfiniteAPIQuery([
    '/email-communication-templates/',
    {
      params: {
        type: 'ADDITIONAL_QUESTIONS',
        individual: false,
      },
    },
  ]);

  const emailCommunicationTemplates = usePrefetchPages(
    emailCommunicationTemplatesPages,
    (data) => data.results
  );

  const handleFormSubmit = async () => {
    if (await hasCandidatesThatAnswered()) {
      setConfirmSendPendingAdditionalQuestionsModalIsOpen(true);
    } else {
      form.submit();
    }
  };

  return (
    <Modal
      okButtonProps={{ htmlType: 'submit', icon: <SendOutlined />, loading }}
      okText="Enviar"
      onOk={() => handleFormSubmit()}
      {...props}
    >
      <ConfirmSendPendingAdditionalQuestionsModal
        key="confirmSendPendingAdditionalQuestionsModal"
        open={confirmSendPendingAdditionalQuestionsModalIsOpen}
        okButtonProps={{ danger: true, type: 'default' }}
        onOk={() => {
          setConfirmSendPendingAdditionalQuestionsModalIsOpen(false);
          form.submit();
        }}
        onCancel={() => setConfirmSendPendingAdditionalQuestionsModalIsOpen(false)}
        onSuccess={() => {
          setConfirmSendPendingAdditionalQuestionsModalIsOpen(false);
        }}
        destroyOnClose
      />
      <Helmet>
        <title>{helmetTitle} | Recrutamento e Seleção</title>
      </Helmet>
      <Form<EmailFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          email_communication_template: null,
          subject: '',
          message: '',
          fromName: config.data?.companyFromName || '',
          replyTo: user.data?.email,
          enableReplyTo: false,
          attachments: [],
        }}
        onFinish={onFinish}
      >
        <EmailForm
          templates={emailCommunicationTemplates.map((emailCommunicationTemplate) => ({
            id: emailCommunicationTemplate.id,
            name: emailCommunicationTemplate.name,
            message: emailCommunicationTemplate.message,
            subject: emailCommunicationTemplate.subject,
            fromName: emailCommunicationTemplate.from_name,
            attachments: emailCommunicationTemplate.attachments,
          }))}
          attachments
          fromName
          replyTo
          customVariables={[
            { value: '[candidato]', label: 'Nome do Candidato' },
            { value: '[empresa]', label: 'Nome da Empresa' },
            ...(jobPostingId
              ? [
                  { value: '[vaga]', label: 'Nome da Vaga' },
                  {
                    value: '[link_perguntas_adicionais]',
                    label: 'Link Perguntas de pré-requisito',
                  },
                ]
              : []),
          ]}
          image
          richEditorRules={[
            { message: 'Campo obrigatório.', required: true },
            {
              validator(_, value) {
                const variablesRegex = /\[vaga\]/;
                if (!jobPostingId && variablesRegex.test(value)) {
                  return Promise.reject(
                    new Error(
                      'Não é possível utilizar a variável [vaga] ao enviar email pelo Banco de Talentos'
                    )
                  );
                }
                return Promise.resolve();
              },
            },
            {
              validator(_, value) {
                const variablesRegex = /\[link_perguntas_adicionais\]/;
                if (!jobPostingId || !variablesRegex.test(value)) {
                  return Promise.reject(
                    new Error(
                      'Para enviar este e-mail é necessário a variável Link Perguntas de pré-requisito'
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        />
      </Form>
    </Modal>
  );
};

export default SendAdditionalQuestionsModal;
